import React, { useMemo } from 'react';
import { Empty, Spin, Tag, Timeline, Typography } from 'antd';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { BagShipmentNodeKind, BagShipmentNodeType } from '../graphql/queries/getBagShipmentHistory';
import { useBagQueryDegradeEnabled } from '../hooks/useBagQueryDegradeEnabled';

// 后端 occurredAt 是 ISO-8601 字符串；项目通用 formatTimestampToDateTime 对 ISO 会返回 Invalid Date
const formatDateTime = (iso: string): string => {
    const d = dayjs(iso);
    return d.isValid() ? d.format('YYYY/MM/DD HH:mm:ss') : '-';
};

const { Text } = Typography;

interface BagShipmentTimelineProps {
    nodes: BagShipmentNodeType[];
    selectedNodeId: string | null;
    onSelect: (nodeId: string) => void;
    loading?: boolean;
}

const NODE_COLOR: Record<BagShipmentNodeKind, string> = {
    CREATED: 'blue',
    DEPARTURE_SCAN: 'green',
    BAG_RESHIP: 'orange',
    LOG_SCAN: 'red',
    CURRENT: 'purple',
    BATCH_MOVE_IN: 'gold',
    BATCH_MOVE_OUT: 'gold',
};

// 携带原/新分拣规则的节点类型：改流向成功，以及改流向失败的扫描失败记录
const SORTING_RULE_KINDS: BagShipmentNodeKind[] = ['BAG_RESHIP', 'LOG_SCAN'];

// 降级时仍展示的低频审计节点（不删除数据源）
const DEGRADED_VISIBLE_KINDS: BagShipmentNodeKind[] = ['CURRENT', 'BAG_RESHIP', 'LOG_SCAN', 'BATCH_MOVE_IN', 'BATCH_MOVE_OUT'];

const TRACK = 'wms.sortingBatch.bagDetail.track';

/**
 * 批次说明行：创建时所属批次；移入（原批次 → 新批次，整包移入为上游批次仍保留）；移出只写原批次——
 * 整包移入的包牌移出后可能还在上游批次，不能一律说「不属于任何批次」
 */
const batchLines = (node: BagShipmentNodeType, t: TFunction): string[] => {
    if (node.kind === 'CREATED') {
        return node.batchName ? [`${t(`${TRACK}.fields.createdBatch`)}: ${node.batchName}`] : [];
    }
    if (node.kind === 'BATCH_MOVE_IN') {
        if (node.isSourceBatchKept && node.sourceBatchName) {
            return [
                `${t(`${TRACK}.fields.upstreamBatch`)}: ${t(`${TRACK}.upstreamKept`, { batch: node.sourceBatchName })}`,
                `${t(`${TRACK}.fields.movedInBatch`)}: ${node.batchName ?? '-'}`,
                `${t(`${TRACK}.fields.note`)}: ${t(`${TRACK}.upstreamNote`)}`,
            ];
        }
        if (node.sourceBatchName) {
            return [
                `${t(`${TRACK}.fields.fromBatch`)}: ${node.sourceBatchName}`,
                `${t(`${TRACK}.fields.toBatch`)}: ${node.batchName ?? '-'}`,
            ];
        }
        return [`${t(`${TRACK}.fields.movedInBatch`)}: ${node.batchName ?? '-'}`];
    }
    if (node.kind === 'BATCH_MOVE_OUT') {
        return [`${t(`${TRACK}.fields.fromBatch`)}: ${node.batchName ?? '-'}`];
    }
    return [];
};

const BagShipmentTimeline: React.FC<BagShipmentTimelineProps> = ({
    nodes,
    selectedNodeId,
    onSelect,
    loading,
}) => {
    const { t } = useTranslation();

    // 包牌查询降级开关：true 时隐藏高频的 CREATED / DEPARTURE_SCAN 节点，
    // 但保留 CURRENT、改流向、扫描失败与批次移入移出（均为低频重要审计事件，降级时也需展示，不删除数据源）
    const bagQueryDegradeEnabled = useBagQueryDegradeEnabled();
    const visibleNodes = useMemo(
        () => (bagQueryDegradeEnabled ? nodes.filter((n) => DEGRADED_VISIBLE_KINDS.includes(n.kind)) : nodes),
        [bagQueryDegradeEnabled, nodes]
    );

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                <Spin />
            </div>
        );
    }

    if (!visibleNodes.length) {
        return <Empty description={t('wms.bags.shipmentHistory.emptyTimeline')} />;
    }

    const kindLabel = (kind: BagShipmentNodeKind): string =>
        t(`wms.bags.shipmentHistory.nodeKind.${kind}`);

    return (
        <Timeline mode="left">
            {[...visibleNodes]
                .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
                .map((node) => {
                const isSelected = node.id === selectedNodeId;
                return (
                    <Timeline.Item key={node.id} color={NODE_COLOR[node.kind]}>
                        <div
                            onClick={() => onSelect(node.id)}
                            style={{
                                cursor: 'pointer',
                                padding: '8px 12px',
                                borderRadius: 6,
                                background: isSelected ? '#f0f5ff' : undefined,
                                border: isSelected ? '1px solid #1677ff' : '1px solid transparent',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                                <Tag color={NODE_COLOR[node.kind]}>{kindLabel(node.kind)}</Tag>
                                <Text type="secondary">{formatDateTime(node.occurredAt)}</Text>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                                {batchLines(node, t).map((line) => (
                                    <Text key={line}>{line}</Text>
                                ))}
                                {node.parcelCount !== null && node.parcelCount !== undefined && (
                                    <Text>
                                        {t('wms.bags.shipmentHistory.parcelCount')}: <Text strong>{node.parcelCount}</Text>
                                    </Text>
                                )}
                                {node.operatorName && (
                                    <Text>
                                        {t('wms.bags.shipmentHistory.operator')}: {node.operatorName}
                                    </Text>
                                )}
                                {node.operatorRole && (
                                    <Text>
                                        {t('wms.bags.shipmentHistory.operatorRole')}: {node.operatorRole}
                                    </Text>
                                )}
                                {node.warehouseName && (
                                    <Text>
                                        {t('wms.bags.shipmentHistory.warehouse')}: {node.warehouseName}
                                    </Text>
                                )}
                                {node.kind === 'LOG_SCAN' && node.originalScanType && (
                                    <Text>
                                        {t('wms.bags.shipmentHistory.originalScanType')}:{' '}
                                        {t(`enum.orderEvent.${node.originalScanType}`, { defaultValue: node.originalScanType })}
                                    </Text>
                                )}
                                {SORTING_RULE_KINDS.includes(node.kind) && (node.oldSortingRule || node.newSortingRule) && (
                                    <>
                                        <Text>
                                            {t('wms.bags.shipmentHistory.originalSortingRule')}: {node.oldSortingRule ?? '-'}
                                        </Text>
                                        <Text>
                                            {t('wms.bags.shipmentHistory.currentSortingRule')}: {node.newSortingRule ?? '-'}
                                        </Text>
                                    </>
                                )}
                                {node.kind === 'LOG_SCAN' && node.failMessageKey && (
                                    <Text type="danger">
                                        {t('wms.bags.shipmentHistory.failReason')}:{' '}
                                        {t(node.failMessageKey, { defaultValue: node.failMessageKey })}
                                    </Text>
                                )}
                            </div>
                        </div>
                    </Timeline.Item>
                );
            })}
        </Timeline>
    );
};

export default BagShipmentTimeline;
